/* eslint-disable max-len */
import Joi from 'joi';

/**
 * @typedef {Object} Post
 * @property {string} resourceId
 * @property {number} includedStartSlotId
 * @property {number} excludedEndSlotId
 * @property {"RemoteWorking"|"Leave"} [type]
 * @property {"Pending"|"Accepted"|"Refused"} [status]
 * @property {number} creationTimestamp
 */

export const postSchema = Joi.object({
  id: Joi.string().required(),
  resourceId: Joi.string().pattern(/^\d+$/).min(1).required(),
  includedStartSlotId: Joi.number().integer().required(),
  excludedEndSlotId: Joi.number().integer().required(),
  type: Joi.string().valid('RemoteWorking', 'Leave').required(),
  status: Joi.string().valid('Pending', 'Accepted', 'Refused').required(),
  creationTimestamp: Joi.number().integer().required(),
  businessDaysSlotCount: Joi.number().integer().required(),
});

/**
 * @typedef {Object} RemoteWorkingPost
 * @property {string} id
 * @property {string|null} address
 * @property {string|null} comment
 */

export const remoteWorkingPostSchema = Joi.object({
  id: Joi.string().required(),
  address: Joi.string().allow(null).required(),
  comment: Joi.string().allow(null, '').required(),
});

/**
 * @typedef {Object} LeavePost
 * @property {string} id
 * @property {"Medical"|"MaternityPaternity"|"Parental"|"SupplementaryBirthLeave"|"School"|"AuthorizedPaidAbsence"|"FamilyEvent"|"Paid"|"Unpaid"|"Recuperation"} type
 * @property {string} comment
 * @property {number} version
 */

export const leavePostSchema = Joi.object({
  id: Joi.string().required(),
  type: Joi.string().valid('Medical', 'MaternityPaternity', 'Parental', 'SupplementaryBirthLeave', 'School', 'AuthorizedPaidAbsence', 'FamilyEvent', 'Paid', 'Unpaid', 'Recuperation').required(),
  comment: Joi.string().allow(null, ''),
  version: Joi.number().integer().required(),
});

/**
 * @typedef {Object} Configuration
 * @property {number} version
 * @property {number} minResourcesOnSite
 * @property {number} availableSeats
 * @property {number} totalResourcesAmount
 * @property {number} shortLeaveNoticePeriod
 * @property {number} longLeaveNoticePeriod
 */

export const configurationSchema = Joi.object({
  version: Joi.number().integer().required(),
  totalResourcesAmount: Joi.number().integer(),
  minResourcesOnSite: Joi.number().integer().min(1).max(99)
    .required()
    .messages({
      'number.min': 'settings.resource.errors.atLeastOne',
      'number.max': 'settings.resource.errors.maxNumDays',
      'number.base': 'settings.resource.errors.rangeNumDays',
    }),
  availableSeats: Joi.number().integer().min(1).max(99)
    .required()
    .messages({
      'number.min': 'settings.resource.errors.atLeastOne',
      'number.max': 'settings.resource.errors.maxNumDays',
      'number.base': 'settings.resource.errors.rangeNumDays',
    }),
  shortLeaveNoticePeriod: Joi.number().integer().min(1).max(99)
    .required()
    .messages({
      'number.min': 'settings.resource.errors.atLeastOne',
      'number.max': 'settings.resource.errors.maxNumDays',
      'number.base': 'settings.resource.errors.rangeNumDays',
    }),
  longLeaveNoticePeriod: Joi.number().integer().min(1).max(99)
    .required()
    .messages({
      'number.min': 'settings.resource.errors.atLeastOne',
      'number.max': 'settings.resource.errors.maxNumDays',
      'number.base': 'settings.resource.errors.rangeNumDays',
    }),
});

/**
 * @typedef {Object} ResourceConfiguration
 * @property {number} id
 * @property {number} [version=1]
 * @property {number} maxRemoteWorkingPostsPerWeek
 * @property {boolean} isSubjectToRemoteWorkingRotationRule
 * @property {boolean} canPostCompensatoryTimeLeave
 * @property {Array.<Object>} partTimeSchedule
 */

export const resourceConfigurationSchema = Joi.object({
  id: Joi.string().required(),
  version: Joi.number().integer().min(1).default(1),
  maxRemoteWorkingPostsPerWeek: Joi.number().integer()
    .min(0).max(5)
    .messages({
      'number.min': 'settings.resource.errors.maxPerWeek',
      'number.max': 'settings.resource.errors.maxPerWeek',
    }),
  isSubjectToRemoteWorkingRotationRule: Joi.boolean(),
  canPostCompensatoryTimeLeave: Joi.boolean(),
  partTimeSchedule: Joi.array().items(Joi.object({
    day: Joi.string(),
    duration: Joi.string(),
  })),
});

export const resourceConfigurationFormSchema = Joi.object({
  maxRemoteWorkingPostsPerWeek: Joi.number().integer()
    .min(0).max(5)
    .messages({
      'number.min': 'settings.resource.errors.maxPerWeek',
      'number.max': 'settings.resource.errors.maxPerWeek',
      'number.base': 'settings.resource.errors.maxPerWeek',
    }),
  isSubjectToRemoteWorkingRotationRule: Joi.boolean(),
  canPostCompensatoryTimeLeave: Joi.boolean(),
  hasPartTime: Joi.boolean(),
  partTimeSchedule: Joi.object({
    Monday: Joi.string().allow(null),
    Tuesday: Joi.string().allow(null),
    Wednesday: Joi.string().allow(null),
    Thursday: Joi.string().allow(null),
    Friday: Joi.string().allow(null),
  }),
});

/**
 * @typedef {Object} Resource
 * @property {string} id
 * @property {string} email
 * @property {string} firstName
 * @property {string} lastName
 * @property {string} photo
 * @property {boolean} isActive
 * @property {string} businessUnit
 * @property {string} prm
 * @property {string} position
 */

export const resourceSchema = Joi.object({
  id: Joi.string().required(),
  email: Joi.string().required(),
  firstName: Joi.string().required(),
  lastName: Joi.string().required(),
  photo: Joi.string(),
  isActive: Joi.boolean(),
  businessUnit: Joi.string().required(),
  prm: Joi.string(),
  position: Joi.string().allow(null),
});
